/**
 * D11: Teacher Assessment Assignments
 * Source: Teacher portal — "Toets klaarzetten" feature
 *
 * AS-2026-07-P2-05 (July 2026): every writer returns `Promise<boolean>` and
 * propagates the result of the underlying `writeJson`. Previously they all
 * returned `Promise<void>` and discarded that boolean, so a completion or
 * abandonment mark could silently fail to persist while the caller treated
 * the operation as successful. This ledger is the authoritative record of
 * who finished a teacher-assigned toets, so a silent failure here is a
 * pedagogical data-loss bug, not a cosmetic one.
 *
 * The `false` return means "the change did not persist". Callers must not
 * present the state change as done.
 *
 * ─── DUAL-MODE (augustus 2026) ──────────────────────────────────────────────
 *
 * Dit domein had als enige in de toetsketen nog geen backend: elke functie
 * gooide `Backend not implemented`. Dat was een halve migratie — met de
 * schakelaar om gingen toetsresultaten (D3) wél naar Supabase, terwijl "toets
 * klaarzetten" crashte. Nu delegeert elke functie bij `USE_SUPABASE` naar
 * `../supabase/teacherAssignmentsBackend`.
 *
 * Het boolean-schrijfcontract geldt in beide modi: `false` = niet opgeslagen
 * (localStorage: quota; Supabase: HTTP-, RLS- of constraint-fout).
 */

import { USE_SUPABASE, readJson, writeJson } from '../helpers';
import * as backend from '../supabase/teacherAssignmentsBackend';
import type { TeacherAssignment } from '../../assessment';
const TEACHER_ASSIGNMENTS_KEY = 'teacher_assignments';

/** Get all teacher-created assessment assignments */
export async function getTeacherAssignments(): Promise<Record<string, TeacherAssignment>> {
  if (USE_SUPABASE) return backend.getTeacherAssignments();
  return readJson<Record<string, TeacherAssignment>>(TEACHER_ASSIGNMENTS_KEY, {});
}

/** Save or update a teacher assessment assignment. Returns persistence success. */
export async function saveTeacherAssignment(id: string, assignment: TeacherAssignment): Promise<boolean> {
  if (USE_SUPABASE) return backend.saveTeacherAssignment(id, assignment);
  const all = readJson<Record<string, TeacherAssignment>>(TEACHER_ASSIGNMENTS_KEY, {});
  all[id] = assignment;
  return writeJson(TEACHER_ASSIGNMENTS_KEY, all);
}

/** Delete a teacher assessment assignment. Returns persistence success. */
export async function deleteTeacherAssignment(id: string): Promise<boolean> {
  if (USE_SUPABASE) return backend.deleteTeacherAssignment(id);
  const all = readJson<Record<string, TeacherAssignment>>(TEACHER_ASSIGNMENTS_KEY, {});
  delete all[id];
  return writeJson(TEACHER_ASSIGNMENTS_KEY, all);
}

/** Archive a teacher assessment assignment. Returns persistence success. */
export async function archiveTeacherAssignment(id: string): Promise<boolean> {
  if (USE_SUPABASE) return backend.archiveTeacherAssignment(id);
  const all = readJson<Record<string, TeacherAssignment>>(TEACHER_ASSIGNMENTS_KEY, {});
  const assignment = all[id];
  if (!assignment) return false;
  assignment.status = 'archived';
  all[id] = assignment;
  return writeJson(TEACHER_ASSIGNMENTS_KEY, all);
}

/**
 * Mark an assignment as completed by a specific student.
 *
 * Returns `true` when the ledger reflects the completion after this call —
 * including the idempotent case where the student was already recorded (no
 * write needed, state already correct). Returns `false` when the assignment
 * does not exist or the write did not persist.
 */
export async function markAssignmentCompletedByStudent(assignmentId: string, studentId: string): Promise<boolean> {
  if (USE_SUPABASE) return backend.markAssignmentCompletedByStudent(assignmentId, studentId);
  const all = readJson<Record<string, Record<string, unknown>>>(TEACHER_ASSIGNMENTS_KEY, {});
  const assignment = all[assignmentId];
  if (!assignment) return false;
  const completedBy = assignment.completedByStudentIds as string[] || [];
  if (completedBy.includes(studentId)) return true;
  assignment.completedByStudentIds = [...completedBy, studentId];
  return writeJson(TEACHER_ASSIGNMENTS_KEY, all);
}

/**
 * F-AS-R1-ASSIGNED-ASSESSMENT-EXIT (May 2026)
 *
 * Mark an assigned assessment as abandoned by a specific student (closed
 * the tab, hit the exit-confirm in the assessment session, or abandoned a
 * recoverable session). Tracked separately from `completedByStudentIds`
 * so the teacher dashboard can distinguish "not yet started" from
 * "started and gave up". Idempotent — a student already in the list is
 * not re-added. Does NOT touch `completedByStudentIds`; a later
 * successful completion will set that flag and the UI is expected to
 * treat completion as the dominant signal.
 *
 * Returns `true` when the ledger reflects the abandonment after this call.
 */
export async function markAssignmentAbandonedByStudent(assignmentId: string, studentId: string): Promise<boolean> {
  if (USE_SUPABASE) return backend.markAssignmentAbandonedByStudent(assignmentId, studentId);
  const all = readJson<Record<string, Record<string, unknown>>>(TEACHER_ASSIGNMENTS_KEY, {});
  const assignment = all[assignmentId];
  if (!assignment) return false;
  const abandoned = assignment.abandonedByStudentIds as string[] || [];
  if (abandoned.includes(studentId)) return true;
  assignment.abandonedByStudentIds = [...abandoned, studentId];
  return writeJson(TEACHER_ASSIGNMENTS_KEY, all);
}